import { useCallback, useState } from 'react';

/**
 * Retenir sur quel sous-onglet on etait.
 *
 * Ouvrir un evenement demonte l'ecran qui le listait ; en revenir le
 * remonte, et son etat repart de zero. On regardait « Organisé », on
 * ouvre une fiche, on ressort — et on se retrouve dans « Inscrit », sans
 * avoir rien demande.
 *
 * La memoire vit hors des composants, a dessein : c'est precisement quand
 * l'ecran se remonte qu'elle sert, et un etat interne disparaitrait avec
 * lui. Meme raison, et meme forme, que les positions de defilement
 * retenues par `useScrollMemory`.
 *
 * Elle est videe au changement de membre ou de spot, comme le reste :
 * voir `SessionContext`.
 */
const onglets = new Map<string, string>();

export function clearTabMemory(): void {
  onglets.clear();
}

export function useTabMemory<T extends string>(
  /** Un nom par ecran : « mes-evenements », « signets »... */
  cle: string,
  defaut: T,
  /** Les onglets connus : une valeur retenue qui n'en fait plus partie
      — un onglet renomme entre deux versions — est ignoree. */
  options: readonly { value: T }[],
): [T, (next: T) => void] {
  const [valeur, setValeur] = useState<T>(() => {
    const retenu = onglets.get(cle);
    return options.some((option) => option.value === retenu) ? (retenu as T) : defaut;
  });

  const poser = useCallback(
    (next: T) => {
      onglets.set(cle, next);
      setValeur(next);
    },
    [cle],
  );

  return [valeur, poser];
}
